import React, { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import BrokerLogin from '../components/BrokerLogin'
import AppHeader from '../components/AppHeader'
import ProfileCompletionBadge from '../components/ProfileCompletionBadge'
import BrokerApplicationKanban from '../components/BrokerApplicationKanban'
import { profileApi } from '../api/profileApi'
import { LogOut, UserCheck, FileText, RefreshCw, LayoutGrid, Home, ChevronDown, ChevronRight, Eye, Mail, Phone, MapPin, Briefcase, DollarSign, Users, Calendar, MessageSquare, BarChart3 } from 'lucide-react'
import BrokerAnalyticsTab from '../components/BrokerAnalyticsTab'
import {
  getMyConnections,
  updateConnection,
  getBrokerLeads,
  updateLeadStatus,
  getConnectionCustomer,
  listApplications,
  type Connection,
  type Lead,
  type Application,
  type CustomerProfile,
} from '../api/brokerApi'
import { fetchStageConfig, TERMINAL_STAGES } from '../constants/applicationStages'
import { getBrokerClientFinancials, type BrokerFinancialSummary } from '../api/incomeExpenseApi'

export default function BrokerPortal() {
  const { user, isAuthenticated, logout, getAccessToken } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const tabParam = searchParams.get('tab')
  const initialTab = tabParam === 'applications' ? 'applications' : tabParam === 'messages' ? 'messages' : tabParam === 'analytics' ? 'analytics' : 'dashboard'
  const [activeTab, setActiveTab] = useState<'dashboard' | 'applications' | 'messages' | 'analytics'>(initialTab)
  const [profileCompletion, setProfileCompletion] = useState<{
    completion_percent: number
    missing: string[]
  } | null>(null)
  const [applications, setApplications] = useState<Application[]>([])


  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'broker' || !getAccessToken()) {
      setProfileCompletion(null)
      return
    }
    profileApi.getProfileCompletion(getAccessToken()!).then(setProfileCompletion).catch(() => setProfileCompletion(null))
    fetchStageConfig(getAccessToken()!).catch(() => {})
  }, [isAuthenticated, user?.userType, getAccessToken])

  // Connection requests (D6)
  const [connections, setConnections] = useState<Connection[]>([])
  const [connectionsLoading, setConnectionsLoading] = useState(false)
  const loadConnections = async () => {
    const token = getAccessToken()
    if (!token) return
    setConnectionsLoading(true)
    try {
      const res = await getMyConnections(token)
      setConnections(res.connections || [])
    } catch { /* ignore */ } finally {
      setConnectionsLoading(false)
    }
  }
  const handleConnectionAction = async (id: string, action: 'accept' | 'reject') => {
    const token = getAccessToken()
    if (!token) return
    try {
      const result = await updateConnection(token, id, action)
      loadConnections()
      if (action === 'accept') {
        loadBrokerLeads()  // Refresh leads — some may have been auto-linked
        if (result.linked_leads && result.linked_leads > 0) {
          setLinkedLeadsMessage(`${result.linked_leads} lead(s) auto-linked to this connection`)
          setTimeout(() => setLinkedLeadsMessage(null), 5000)
        }
      }
    } catch (err) {
      console.error('Failed to update connection:', err)
    }
  }

  // Customer detail expansion (Gap 1)
  const [expandedConnection, setExpandedConnection] = useState<string | null>(null)
  const [customerDetail, setCustomerDetail] = useState<CustomerProfile | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [linkedLeadsMessage, setLinkedLeadsMessage] = useState<string | null>(null)
  const [connectionFinancials, setConnectionFinancials] = useState<Record<string, BrokerFinancialSummary>>({})

  const loadCustomerDetail = async (connectionId: string) => {
    if (expandedConnection === connectionId) {
      setExpandedConnection(null)
      setCustomerDetail(null)
      return
    }
    const token = getAccessToken()
    if (!token) return
    setExpandedConnection(connectionId)
    setDetailLoading(true)
    try {
      const profile = await getConnectionCustomer(token, connectionId)
      setCustomerDetail(profile)
      // Also load financials for this customer
      const conn = connections.find(c => c.id === connectionId)
      if (conn?.customer_id) {
        getBrokerClientFinancials(token, conn.customer_id)
          .then(fin => setConnectionFinancials(prev => ({ ...prev, [connectionId]: fin })))
          .catch(() => {})
      }
    } catch (err) {
      console.error('Failed to load customer detail:', err)
      setCustomerDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  // Assigned leads (D18)
  const [brokerLeads, setBrokerLeads] = useState<Lead[]>([])
  const [leadsLoading, setLeadsLoading] = useState(false)
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set())
  const loadBrokerLeads = async () => {
    const token = getAccessToken()
    if (!token) return
    setLeadsLoading(true)
    try {
      const res = await getBrokerLeads(token)
      setBrokerLeads(res.leads || [])
    } catch { /* ignore */ } finally {
      setLeadsLoading(false)
    }
  }
  const handleLeadStatus = async (id: string, status: string) => {
    const token = getAccessToken()
    if (!token) return
    try {
      await updateLeadStatus(token, id, status)
      loadBrokerLeads()
    } catch (err) {
      console.error('Failed to update lead:', err)
    }
  }

  useEffect(() => {
    if (isAuthenticated && user?.userType === 'broker' && getAccessToken()) {
      loadConnections()
      loadBrokerLeads()
      listApplications(getAccessToken()!).then(res => setApplications(res.applications || [])).catch(() => {})

    }
  }, [isAuthenticated, user?.userType])

  // Computed dashboard stats from real API data
  const stats = useMemo(() => ({
    activeClients: connections.filter(c => c.status === 'active').length,
    pendingRequests: connections.filter(c => c.status === 'pending').length,
    closedDeals: applications.filter(a => TERMINAL_STAGES.includes(a.stage)).length,
    totalLeads: brokerLeads.length,
  }), [connections, applications, brokerLeads])

  // Recent clients from active connections, sorted by most recent first
  const recentClients = useMemo(() =>
    connections
      .filter(c => c.status === 'active')
      .sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
        return dateB - dateA
      })
      .slice(0, 5)
      .map(c => ({
        id: c.id,
        name: c.customer_name || 'Customer',
        status: 'Active' as const,
        loanAmount: 0,
        stage: 'Connected',
      })),
    [connections]
  )

  // Recent activity from connections and leads
  const recentActivity = useMemo(() => {
    const items: { id: string; type: string; client: string; time: string }[] = []

    // Recent pending connections = new_request
    connections
      .filter(c => c.status === 'pending')
      .forEach(c => items.push({
        id: c.id,
        type: 'new_request',
        client: c.customer_name || 'Customer',
        time: c.created_at ? formatRelativeTime(c.created_at) : '',
      }))

    // Recent leads
    brokerLeads
      .slice(0, 5)
      .forEach(l => items.push({
        id: l.id,
        type: l.status === 'converted' ? 'loan_approved' : 'new_request',
        client: l.name || l.email || 'Lead',
        time: l.created_at ? formatRelativeTime(l.created_at) : '',
      }))

    return items.slice(0, 5)
  }, [connections, brokerLeads])

  // Require authentication for broker portal
  if (!isAuthenticated || user?.userType !== 'broker') {
    return <BrokerLogin />
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin} minutes ago`
    const diffHrs = Math.floor(diffMin / 60)
    if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`
    const diffDays = Math.floor(diffHrs / 24)
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        rightContent={
          <>
            <span className="text-gray-600">Broker Portal</span>
            <Link to="/customer" className="text-sm text-primary-600 hover:text-primary-700">
              Switch to Customer View
            </Link>
            <Link to="/broker/profile" className="text-sm text-primary-600 hover:text-primary-700">
              My Profile
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-semibold">{(user?.username || user?.email || user?.full_name || 'U').substring(0, 2).toUpperCase()}</span>
              </div>
              <span className="text-sm font-medium">{user?.username || user?.email || user?.full_name || 'User'}</span>
              <button
                onClick={logout}
                className="ml-2 text-gray-600 hover:text-red-600 transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {profileCompletion && profileCompletion.completion_percent < 100 && (
          <div className="mb-6">
            <ProfileCompletionBadge
              completionPercent={profileCompletion.completion_percent}
              missing={profileCompletion.missing}
              userRole="broker"
            />
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'dashboard' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Home className="w-4 h-4" /> Dashboard
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'applications' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutGrid className="w-4 h-4" /> Applications
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'messages' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <MessageSquare className="w-4 h-4" /> Messages
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BarChart3 className="w-4 h-4" /> Analytics
          </button>
        </div>

        {/* Messages Tab — redirect to shared messages page */}
        {activeTab === 'messages' && (
          <div className="text-center py-16">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Broker Messages</h3>
            <p className="text-gray-500 mb-6">Communicate with your clients securely through LoanLoop.</p>
            <Link
              to="/messages"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Open Messages
            </Link>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <BrokerAnalyticsTab
            connections={connections}
            leads={brokerLeads}
            applications={applications}
          />
        )}

        {/* Applications Tab — Kanban Board */}
        {activeTab === 'applications' && (
          <div style={{ minHeight: '70vh' }}>
            <BrokerApplicationKanban token={getAccessToken()!} />
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (<>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {(user?.username || user?.email || user?.full_name || 'Broker').split(' ')[0]}!
          </h1>
          <p className="text-gray-600 mt-2">Here's what's happening with your clients today</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">Active Clients</p>
                <p className="text-3xl font-bold mt-2">{stats.activeClients}</p>
              </div>
              <svg className="w-12 h-12 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">Pending Requests</p>
                <p className="text-3xl font-bold mt-2">{stats.pendingRequests}</p>
              </div>
              <svg className="w-12 h-12 text-green-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">Closed This Month</p>
                <p className="text-3xl font-bold mt-2">{stats.closedDeals}</p>
              </div>
              <svg className="w-12 h-12 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100">Total Leads</p>
                <p className="text-3xl font-bold mt-2">{stats.totalLeads}</p>
              </div>
              <svg className="w-12 h-12 text-orange-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Pending Connection Requests (D6) */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-600" />
              Connection Requests
            </h2>
            <button onClick={loadConnections} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
          {connectionsLoading ? (
            <p className="text-gray-500 text-sm">Loading...</p>
          ) : connections.filter(c => c.status === 'pending').length === 0 ? (
            <p className="text-gray-500 text-sm">No pending connection requests</p>
          ) : (
            <div className="space-y-3">
              {connections.filter(c => c.status === 'pending').map((conn) => (
                <div key={conn.id} className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{conn.customer_name || 'Customer'}</p>
                    {conn.message && <p className="text-sm text-gray-600 mt-1">{conn.message}</p>}
                    <p className="text-xs text-gray-500 mt-1">{conn.created_at ? new Date(conn.created_at).toLocaleDateString() : ''}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleConnectionAction(conn.id, 'accept')}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleConnectionAction(conn.id, 'reject')}
                      className="px-3 py-1.5 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {linkedLeadsMessage && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              {linkedLeadsMessage}
            </div>
          )}
          {connections.filter(c => c.status === 'active').length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Active Connections</h3>
              <div className="space-y-2">
                {connections.filter(c => c.status === 'active').map((conn) => (
                  <div key={conn.id} className="border border-green-200 bg-green-50 rounded-lg overflow-hidden">
                    <div className="p-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{conn.customer_name || 'Customer'}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Active</span>
                        <button
                          onClick={() => loadCustomerDetail(conn.id)}
                          className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {expandedConnection === conn.id ? 'Hide' : 'Details'}
                        </button>
                      </div>
                    </div>
                    {expandedConnection === conn.id && (
                      <div className="border-t border-green-200 bg-white p-4">
                        {detailLoading ? (
                          <p className="text-sm text-gray-500">Loading customer details...</p>
                        ) : customerDetail ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">{customerDetail.email || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">{customerDetail.phone_number || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">{customerDetail.address || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Briefcase className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">{customerDetail.employment_status || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <DollarSign className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {customerDetail.annual_income
                                  ? `$${customerDetail.annual_income.toLocaleString()} p.a.`
                                  : '—'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Users className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {customerDetail.dependants !== null
                                  ? `${customerDetail.dependants} dependant(s)`
                                  : '—'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {customerDetail.date_of_birth
                                  ? new Date(customerDetail.date_of_birth).toLocaleDateString()
                                  : '—'}
                              </span>
                            </div>
                            {customerDetail.comparison_snapshot && (
                              <div className="sm:col-span-2 mt-2 p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-medium text-gray-500 mb-1">Loan Comparison at Connection</p>
                                <p className="text-sm text-gray-700">
                                  Loan: ${(customerDetail.comparison_snapshot as Record<string, unknown>).loan_amount
                                    ? Number((customerDetail.comparison_snapshot as Record<string, unknown>).loan_amount).toLocaleString()
                                    : '—'}
                                  {' | '}
                                  Property: ${(customerDetail.comparison_snapshot as Record<string, unknown>).property_value
                                    ? Number((customerDetail.comparison_snapshot as Record<string, unknown>).property_value).toLocaleString()
                                    : '—'}
                                </p>
                              </div>
                            )}
                            {/* Financial Summary */}
                            {(() => {
                              const fin = connectionFinancials[conn.id]
                              if (!fin) return null
                              if (!fin.shared) return (
                                <div className="sm:col-span-2 mt-2 p-3 bg-gray-50 rounded-lg">
                                  <p className="text-xs font-medium text-gray-500">Financial Profile: <span className="text-amber-600">Not shared</span></p>
                                </div>
                              )
                              return (
                                <div className="sm:col-span-2 mt-2 p-3 bg-gray-50 rounded-lg">
                                  <p className="text-xs font-medium text-gray-500 mb-1">Financial Profile</p>
                                  <div className="grid grid-cols-3 gap-2 text-sm">
                                    <div>
                                      <span className="text-xs text-gray-500">Income</span>
                                      <p className="font-medium text-green-700">${fin.summary.total_monthly_income.toLocaleString('en-AU', { maximumFractionDigits: 0 })}/mo</p>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500">Expenses</span>
                                      <p className="font-medium text-red-700">${fin.summary.total_monthly_expenses.toLocaleString('en-AU', { maximumFractionDigits: 0 })}/mo</p>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500">Net</span>
                                      <p className={`font-medium ${fin.summary.net_monthly_cashflow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                        ${fin.summary.net_monthly_cashflow.toLocaleString('en-AU', { maximumFractionDigits: 0 })}/mo
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )
                            })()}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No profile data available yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Assigned Leads (D18) — grouped by email like Admin portal */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Assigned Leads ({brokerLeads.length})
            </h2>
            <button onClick={loadBrokerLeads} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
          {leadsLoading ? (
            <p className="text-gray-500 text-sm">Loading...</p>
          ) : brokerLeads.length === 0 ? (
            <p className="text-gray-500 text-sm">No leads assigned yet</p>
          ) : (() => {
            // Group leads by email
            const grouped = new Map<string, Lead[]>()
            brokerLeads.forEach((lead) => {
              const key = lead.email || lead.id
              if (!grouped.has(key)) grouped.set(key, [])
              grouped.get(key)!.push(lead)
            })
            const groups = Array.from(grouped.entries())

            return (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name / Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {groups.map(([email, groupLeads]) => {
                      const isMulti = groupLeads.length > 1
                      const isExpanded = expandedEmails.has(email)
                      const first = groupLeads[0]

                      if (!isMulti) {
                        return (
                          <tr key={first.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{first.name || '—'}</div>
                              <div className="text-xs text-gray-500">{first.email || '—'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{first.phone || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{first.loan_purpose || '—'}</td>
                            <td className="px-4 py-3">
                              <select value={first.status} onChange={(e) => handleLeadStatus(first.id, e.target.value)} className="text-xs border border-gray-300 rounded px-1 py-0.5">
                                <option value="new">new</option>
                                <option value="contacted">contacted</option>
                                <option value="qualified">qualified</option>
                                <option value="converted">converted</option>
                                <option value="dead">dead</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{first.source || '—'}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{first.created_at ? new Date(first.created_at).toLocaleDateString() : '—'}</td>
                            <td className="px-4 py-3">
                              {first.status !== 'converted' && first.status !== 'dead' && (
                                <button
                                  onClick={() => window.location.href = `/broker/select-customer?lead_id=${first.id}&email=${encodeURIComponent(first.email || '')}&name=${encodeURIComponent(first.name || '')}&phone=${encodeURIComponent(first.phone || '')}`}
                                  className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                >
                                  Start App
                                </button>
                              )}
                            </td>
                          </tr>
                        )
                      }

                      return (
                        <React.Fragment key={email}>
                          <tr
                            className="hover:bg-blue-50 cursor-pointer bg-blue-50/30"
                            onClick={() => setExpandedEmails(prev => {
                              const next = new Set(prev)
                              next.has(email) ? next.delete(email) : next.add(email)
                              return next
                            })}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                <span className="text-sm font-medium text-gray-900">{first.name || '—'}</span>
                                <span className="bg-blue-100 text-blue-700 text-xs font-bold px-1.5 py-0.5 rounded-full ml-1">{groupLeads.length}</span>
                              </div>
                              <div className="text-xs text-gray-500 ml-5">{email}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{first.phone || '—'}</td>
                            <td colSpan={5} className="px-4 py-3 text-sm text-gray-500">
                              {groupLeads.length} enquiries — click to {isExpanded ? 'collapse' : 'expand'}
                            </td>
                          </tr>
                          {isExpanded && groupLeads.map((lead) => (
                            <tr key={lead.id} className="hover:bg-gray-50 bg-gray-50/50">
                              <td className="px-4 py-2 pl-10">
                                <div className="text-xs text-gray-500">{lead.email}</div>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600">{lead.phone || '—'}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">{lead.loan_purpose || '—'}</td>
                              <td className="px-4 py-2">
                                <select value={lead.status} onChange={(e) => handleLeadStatus(lead.id, e.target.value)} className="text-xs border border-gray-300 rounded px-1 py-0.5">
                                  <option value="new">new</option>
                                  <option value="contacted">contacted</option>
                                  <option value="qualified">qualified</option>
                                  <option value="converted">converted</option>
                                  <option value="dead">dead</option>
                                </select>
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-500">{lead.source || '—'}</td>
                              <td className="px-4 py-2 text-xs text-gray-500">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}</td>
                              <td className="px-4 py-2">
                                {lead.status !== 'converted' && lead.status !== 'dead' && (
                                  <button
                                    onClick={() => window.location.href = `/broker/select-customer?lead_id=${lead.id}&email=${encodeURIComponent(lead.email || '')}&name=${encodeURIComponent(lead.name || '')}&phone=${encodeURIComponent(lead.phone || '')}`}
                                    className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                                  >
                                    Start App
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          })()}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Clients */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Recent Clients</h2>
              <button onClick={() => setActiveTab('applications')} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                View All
              </button>
            </div>
            <div className="space-y-4">
              {recentClients.map((client) => (
                <div key={client.id} className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 font-semibold">
                          {client.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{client.name}</h3>
                        <p className="text-sm text-gray-600">{formatCurrency(client.loanAmount)} loan</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      client.status === 'Active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {client.status}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    {client.stage}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
              <button onClick={() => setActiveTab('applications')} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                View All
              </button>
            </div>
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activity.type === 'new_request' ? 'bg-blue-100' :
                    activity.type === 'document_shared' ? 'bg-green-100' :
                    activity.type === 'message' ? 'bg-purple-100' :
                    'bg-orange-100'
                  }`}>
                    {activity.type === 'new_request' && (
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    )}
                    {activity.type === 'document_shared' && (
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                    {activity.type === 'message' && (
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    )}
                    {activity.type === 'loan_approved' && (
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{activity.client}</span>
                      {' '}
                      {activity.type === 'new_request' && 'sent a new connection request'}
                      {activity.type === 'document_shared' && 'shared documents with you'}
                      {activity.type === 'message' && 'sent you a message'}
                      {activity.type === 'loan_approved' && 'loan was approved'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <button onClick={() => navigate('/broker/select-customer')} className="card hover:shadow-lg transition-shadow text-left">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">New Client</h3>
                <p className="text-sm text-gray-600">Add a new client</p>
              </div>
            </div>
          </button>

          <button onClick={() => navigate('/broker/select-customer')} className="card hover:shadow-lg transition-shadow text-left">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Compare Loans</h3>
                <p className="text-sm text-gray-600">Run comparison</p>
              </div>
            </div>
          </button>

          <button onClick={() => setActiveTab('analytics')} className="card hover:shadow-lg transition-shadow text-left">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">View Reports</h3>
                <p className="text-sm text-gray-600">Analytics & insights</p>
              </div>
            </div>
          </button>
        </div>
        </>)}
      </div>
    </div>
  )
}
